import type { ReactNode } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'
import { Surface } from './primitives'
import { tokens } from '../theme'

/**
 * Placeholders shaped like the content that is coming.
 *
 * A screen that renders nothing until its query resolves reads as an empty
 * screen: "no regions" and "regions, in a moment" look identical, and the
 * answer changes under the reader a second later. A block the size of the
 * row that is on its way says which of the two it is, and holds the layout
 * still so nothing jumps when the data lands.
 *
 * Shapes, not spinners. A spinner says only that something is happening; a
 * skeleton also says what, and how much of it.
 */

/** One placeholder block. `pc-shimmer` is defined once, in the theme. */
export function Skeleton({
  width = '100%', height = 12, radius = tokens.radius.sm, sx,
}: {
  width?: number | string
  height?: number | string
  radius?: number
  sx?: SxProps<Theme>
}) {
  return (
    <Box
      aria-hidden
      sx={{
        width, height, borderRadius: `${radius}px`, flex: 'none',
        backgroundColor: tokens.color.ink100,
        position: 'relative', overflow: 'hidden',
        '&::after': {
          content: '""', position: 'absolute', inset: 0,
          background: `linear-gradient(90deg, transparent, ${tokens.color.ink0}, transparent)`,
          opacity: 0.7,
          animation: 'pc-shimmer 1.4s ease-in-out infinite',
        },
        ...sx,
      }}
    />
  )
}

/**
 * Wraps a set of placeholders so assistive technology is told the screen is
 * busy rather than empty, and so tests have one thing to wait for.
 */
function Busy({ label, children, sx }: { label: string; children: ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Box role="status" aria-busy="true" aria-label={label} data-testid="skeleton" sx={sx}>
      {children}
    </Box>
  )
}

/**
 * Rows for the grid tables — Keys, Users, Usage, the plot and field tables.
 *
 * Emitted as bare cells because every one of those tables is a single CSS
 * grid with sticky headers: a wrapper element around each row would take the
 * cells out of the grid and the columns would stop lining up. The widths
 * vary by position so the block does not read as a barcode, and they are
 * derived rather than random so a re-render does not reshuffle them.
 */
export function SkeletonGridRows({
  columns, rows = 6, height = 48, align = [],
}: {
  columns: number
  rows?: number
  height?: number
  /** Column indexes whose content is right-aligned, matching the real table. */
  align?: number[]
}) {
  const widths = ['62%', '45%', '78%', '38%', '55%', '70%', '48%']
  // One wrapper for the whole set rather than one per row: `display: contents`
  // keeps the cells in the table's grid, and a reader should hear "loading"
  // once, not once per row.
  return (
    <Busy label="Loading rows" sx={{ display: 'contents' }}>
      {Array.from({ length: rows * columns }, (_, i) => {
        const r = Math.floor(i / columns)
        const c = i % columns
        return (
          <Box
            key={i}
            sx={{
              height, display: 'flex', alignItems: 'center', minWidth: 0,
              justifyContent: align.includes(c) ? 'flex-end' : 'flex-start',
              borderTop: `1px solid ${tokens.color.ink200}`,
            }}
          >
            <Skeleton width={widths[(r + c * 3) % widths.length]} height={10} />
          </Box>
        )
      })}
    </Busy>
  )
}

/**
 * Cards for the Regions list: the same grid as a real row, so the list does
 * not reflow when the rows arrive.
 */
export function SkeletonCards({
  count = 4, columns, height = 64,
}: { count?: number; columns: string; height?: number }) {
  return (
    <Busy label="Loading regions" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {Array.from({ length: count }, (_, i) => (
        <Surface
          key={i}
          sx={{
            display: 'grid', gridTemplateColumns: columns, alignItems: 'center',
            gap: 5, px: 5, py: 4, minHeight: height,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton width={`${52 + ((i * 13) % 30)}%`} height={13} />
            <Skeleton width={`${34 + ((i * 17) % 25)}%`} height={10} />
          </Box>
          <Skeleton width="70%" height={11} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton width={56} height={13} />
            <Skeleton width={44} height={9} />
          </Box>
          <Skeleton width={64} height={11} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Skeleton width={88} height={20} radius={tokens.radius.pill} />
          </Box>
        </Surface>
      ))}
    </Busy>
  )
}

/**
 * A whole screen that has not arrived yet: heading, then panels.
 *
 * Stands in for the centred spinner the heavy screens used to show. The
 * spinner was honest but placeless — the page appeared all at once somewhere
 * else on the screen, which is the jump this is here to stop.
 */
export function SkeletonScreen({
  panels = 2, wide = false,
}: {
  /** How many blocks of content follow the heading. */
  panels?: number
  /** A map-first screen: one tall panel beside a narrow column. */
  wide?: boolean
}) {
  return (
    <Busy label="Loading" sx={{ flex: 1, minHeight: 0, px: 8, py: 7, display: 'flex', flexDirection: 'column', gap: 5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Skeleton width={110} height={9} />
        <Skeleton width={360} height={26} radius={tokens.radius.md} />
      </Box>
      {wide ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 380px', gap: 5 }}>
          <Skeleton height="100%" radius={tokens.radius.md} sx={{ minHeight: 280 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} height={48} radius={tokens.radius.md} />
            ))}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {Array.from({ length: panels }, (_, i) => (
            <Skeleton key={i} height={i === 0 ? 180 : 120} radius={tokens.radius.md} />
          ))}
        </Box>
      )}
    </Busy>
  )
}
